/**
 * Erster Start: Gerät anmelden, Wiederherstellungs-Code zeigen,
 * Anleitung „Zum Home-Bildschirm“ und Mitteilungen aktivieren.
 */
import { useState } from 'react';
import { Share, SquarePlus } from 'lucide-react';
import { recoverDevice, registerDevice } from '../lib/store';
import { enablePush, isIos, isStandalone, pushSupport } from '../lib/push';
import { Button, Card } from '../ui/kit';

type Step = 'hello' | 'code' | 'install' | 'push' | 'recover';

export function Onboarding({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState<Step>('hello');
  const [code, setCode] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pushMsg, setPushMsg] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const start = async (force = false) => {
    // iPhone: Safari und die installierte App haben getrennte Speicher → erst installieren, dann einrichten
    if (!force && isIos() && !isStandalone()) return setStep('install');
    setBusy(true);
    setError(null);
    try {
      const res = await registerDevice(isIos() ? 'iPhone' : 'Gerät');
      setCode(res.recoveryCode);
      setStep('code');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Keine Verbindung zum Server.');
    } finally {
      setBusy(false);
    }
  };

  const recover = async () => {
    setBusy(true);
    setError(null);
    try {
      await recoverDevice(input.trim(), isIos() ? 'iPhone' : 'Gerät');
      setStep(isStandalone() ? 'push' : 'install');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const afterCode = () => (pushSupport() === 'ok' ? setStep('push') : onDone());

  const allowPush = () => {
    setPushMsg('Einen Moment …');
    enablePush()
      .then((r) => {
        setPushMsg(r.message);
        if (r.ok) setTimeout(onDone, 900);
      })
      .catch((e: unknown) => setPushMsg(e instanceof Error ? e.message : String(e)));
  };

  return (
    <div className="paper pt-safe pb-safe flex min-h-dvh flex-col px-5">
      <div className="flex flex-1 flex-col justify-center">
        {step === 'hello' && (
          <div className="rise">
            <img src="/icon.svg" alt="" className="h-20 w-20 rounded-[22px] shadow-sm" />
            <h1 className="font-display mt-6 text-[40px] leading-[1.05]">Deine Pflanzen, gut versorgt.</h1>
            <p className="mt-3 text-[16px] text-ink/75">
              Erinnert ans <b>Prüfen</b> statt blind ans Gießen, lernt, wie oft deine Pflanzen wirklich Wasser brauchen und sagt dir, wann Fenster und Vorhang auf oder zu sollen.
            </p>
            <p className="mt-4 text-sm text-muted">Calathea, Dieffenbachia und Bogenhanf sind schon angelegt.</p>
            {error && <p className="mt-4 text-sm text-alert">{error}</p>}
            <Button onClick={() => void start()} disabled={busy} className="mt-8 w-full">
              {busy ? 'Richte ein …' : 'Los geht\'s'}
            </Button>
            <button type="button" onClick={() => setStep('recover')} className="mt-3 w-full py-2 text-sm font-semibold text-moss">
              Ich habe schon einen Code (anderes Gerät)
            </button>
          </div>
        )}

        {step === 'code' && code && (
          <div className="rise">
            <h1 className="font-display text-[30px] leading-tight text-balance">Dein Wieder­herstellungs-Code</h1>
            <p className="mt-2 text-[15px] text-ink/75">
              Damit holst du alles zurück, falls du das iPhone wechselst oder die App löschst. Es gibt kein Passwort, nur diesen Code.
            </p>
            <div className="mt-5 rounded-2xl border-2 border-dashed border-moss/40 bg-moss-soft px-4 py-5 text-center">
              <div className="text-[11px] font-semibold tracking-wide text-moss/80 uppercase">Wiederherstellungs-Code</div>
              <div className="mt-1 font-mono text-[19px] font-semibold tracking-wider break-all text-moss select-all">{code}</div>
            </div>
            <Button
              tone="ghost"
              className="mt-3 w-full"
              onClick={() => {
                void navigator.clipboard?.writeText(code).then(() => setSaved(true));
              }}
            >
              {saved ? 'Kopiert ✓ – jetzt z. B. in Notizen oder Passwörter speichern' : 'Code kopieren'}
            </Button>
            <Button onClick={afterCode} className="mt-6 w-full">
              Ich habe ihn gesichert
            </Button>
          </div>
        )}

        {step === 'install' && (
          <div className="rise">
            <h1 className="font-display text-[32px] leading-tight">Zum Home-Bildschirm</h1>
            <p className="mt-2 text-[15px] text-ink/75">Nur so kann dich die App mit Mitteilungen erinnern.</p>
            <Card className="mt-5 space-y-4">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-water-soft text-water">
                  <Share className="h-5 w-5" />
                </span>
                <p className="text-[15px]">
                  1. Unten in Safari auf <b>Teilen</b> tippen
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-water-soft text-water">
                  <SquarePlus className="h-5 w-5" />
                </span>
                <p className="text-[15px]">
                  2. <b>Zum Home-Bildschirm</b> wählen – „Als Web-App öffnen“ eingeschaltet lassen
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-moss-soft text-lg">🌿</span>
                <p className="text-[15px]">3. Die App über das neue Symbol auf dem Home-Bildschirm öffnen – dort geht’s weiter.</p>
              </div>
            </Card>
            <button type="button" onClick={() => void start(true)} className="mt-6 w-full py-2 text-sm font-semibold text-muted">
              Nur im Browser ausprobieren (ohne Mitteilungen)
            </button>
          </div>
        )}

        {step === 'push' && (
          <div className="rise">
            <div className="text-5xl">🔔</div>
            <h1 className="font-display mt-4 text-[32px] leading-tight">Erinnerungen erlauben</h1>
            <p className="mt-2 text-[15px] text-ink/75">
              Einmal am Morgen, wenn eine Pflanze geprüft werden will – und bei Gewitter, Sturm oder praller Sonne, wenn Fenster oder Vorhang offen sind. Sonst nichts.
            </p>
            <Button onClick={allowPush} className="mt-8 w-full">
              Mitteilungen erlauben
            </Button>
            {pushMsg && <p className="mt-3 text-sm text-muted">{pushMsg}</p>}
            <button type="button" onClick={onDone} className="mt-3 w-full py-2 text-sm font-semibold text-muted">
              Später
            </button>
          </div>
        )}

        {step === 'recover' && (
          <div className="rise">
            <h1 className="font-display text-[32px] leading-tight">Mit Code verbinden</h1>
            <p className="mt-2 text-[15px] text-ink/75">Gib den Wiederherstellungs-Code ein, den du beim ersten Einrichten gesichert hast.</p>
            <input
              className="mt-5 min-h-12 w-full rounded-2xl border border-line bg-card px-3.5 font-mono text-[16px] uppercase"
              placeholder="PFLZ-XXXX-XXXX-XXXX-XXXX"
              value={input}
              autoCapitalize="characters"
              autoCorrect="off"
              onChange={(e) => setInput(e.target.value)}
            />
            {error && <p className="mt-3 text-sm text-alert">{error}</p>}
            <Button onClick={() => void recover()} disabled={busy || input.trim().length < 10} className="mt-4 w-full">
              {busy ? 'Verbinde …' : 'Verbinden'}
            </Button>
            <button type="button" onClick={() => setStep('hello')} className="mt-3 w-full py-2 text-sm font-semibold text-muted">
              Zurück
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
